import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useFetchList, useReload, useTaskManager } from '../../store'
import useSession from 'hooks/useSession'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import CopyToClipboard from '@jimmyboys/liita-ui/dist/components/CopyToClipboard'
import LightBoxButton from 'components/LightBoxButton/LightBoxButton'
import { renderDocuments } from 'components/widgets/DocumentsWidget'

import {
	ClinicalProcess,
	Task,
	Document,
	TrustNotification,
	Appointment,
} from 'models'

import styles from 'controllers/ContextualController/ContextualActionsController.module.sass'
import { renderNotes } from 'components/widgets/NotesWidgets/NotesWidget'
import Note from 'models/Note'
import { renderTasks, mapTasks } from 'components/widgets/TasksWidget'
import { renderNotifications } from 'components/widgets/NotificationsWidget'
import {
	AppointmentPendingStatus,
	ClinicalStatus,
	InjuredState,
	Segment,
} from 'utils/enums'
import { shouldAllowManualIppCreation } from 'utils/allowManualIppCreation'
import {
	APPOINTMENT_PROTOCOLS,
	MEDICAL_ACT_PROTOCOLS,
} from 'dialogs/DataEditingDialogs/EditProcessProtocolDialog/EditProcessProtocolDialog'

export const PORTAL_TRUST_ENDPOINT =
	window.REACT_APP_PORTAL_TRUST_ENDPOINT ||
	process.env.REACT_APP_PORTAL_TRUST_ENDPOINT

export const CATALOG_WEBAPP_ENDPOINT =
	window.REACT_APP_CATALOG_WEBAPP_ENDPOINT ||
	process.env.REACT_APP_CATALOG_WEBAPP_ENDPOINT

const ContextualActionsController = () => {
	const params = useParams()
	const {
		showDialog,
		i18n,
		openFile,
		addNote,
		deleteNote,
		updateNote,
		pushNotification,
	} = useGlobal()
	const reload = useReload()
	const { user: sessionUser } = useSession()
	const { openSingleTask } = useTaskManager()
	const userRoles = sessionUser?.roles || []

	const isAdministrative = sessionUser?.roles.includes('manager')
	const isAdmin = sessionUser?.roles?.some(
		(role) => role.toLowerCase() === 'admin'
	)

	const isFinance = sessionUser?.roles?.some(
		(role) => role.toLowerCase() === 'finance'
	)

	const isIbReadOnly = sessionUser?.roles?.some(
		(role) => role.toLowerCase() === 'ibreadonly'
	)

	const processUri = `/clinical_processes/${params.clinicalProcessId}`
	const commentsUri = `/clinical_processes/${params.clinicalProcessId}/comments`
	const processTasksUri = `/clinical_processes/${params.clinicalProcessId}/tasks`
	const documentsUri = `/clinical_processes/${params.clinicalProcessId}/documents`
	const processNotesUri = `/notes/clinical_process/${params.clinicalProcessId}`
	const tasksUri = `/clinical_processes/${params.clinicalProcessId}/tasks`
	const notificationsUri = `/clinical_processes/${params.clinicalProcessId}/notifications`
	const appointmentsUri = `/clinical_processes/${params.clinicalProcessId}/appointments`
	const openTransferRequisitionUri = `/clinical_processes/${params.clinicalProcessId}/open_transfer_requisition`

	const {
		contents: [
			rawProcessData,
			processTasks,
			documents = [],
			notes = [],
			tasks = [],
			notifications = [],
			appointments = [],
			hasOpenTransferRequisition = false,
		],
	} = useFetchList(
		[
			processUri,
			processTasksUri,
			documentsUri,
			processNotesUri,
			tasksUri,
			notificationsUri,
			appointmentsUri,
			openTransferRequisitionUri,
		],
		[
			null,
			Task.fromJSON,
			Document.fromJSON,
			Note.fromJSON,
			Task.fromJSON,
			TrustNotification.fromJSON,
			Appointment.fromJSON,
			null,
		]
	)
	const process = ClinicalProcess.fromJSON(rawProcessData || {})

	const hasProcessForwardingTask =
		(processTasks || []).findIndex(
			({ type }) => type === 'ProcessProviderForwarding'
		) >= 0

	/* CM-201 Block transfer if pending appointment with past date exists */
	const hasPendingAppointmentsWithPastDates = (appointments || []).some(
		(appointment) => {
			const pendingStatuses = Object.values(AppointmentPendingStatus)
			const isPendingStatus = pendingStatuses.includes(
				appointment?.status?.description
			)
			const hasPastDate =
				appointment?.date?.date instanceof Date &&
				appointment.date.date < new Date()

			return isPendingStatus && hasPastDate
		}
	)

	const processBranch = useMemo(() => {
		return process?.accident?.segment?.abbreviation ?? 'AT'
	}, [process?.accident?.segment?.abbreviation])
	const insertOptions = [
		{
			value: 'document',
			text: i18n`Insert document`,
			action: () => {
				showDialog(
					'DocumentDialog',
					i18n`Document Submission Dialog`,
					{
						processId: params?.clinicalProcessId,
					},
					false,
					undefined,
					'center',
					'document-dialog'
				)
					.then(() => {
						reload([])
					})
					.catch(() => null)
			},
		},
		{
			value: 'comment',
			text: i18n`Insert commentary`,
			action: () => {
				showDialog(
					'CommentDialog',
					i18n`Comment Dialog`,
					{
						processId: params?.clinicalProcessId,
						processLocation: process?.provider?.id,
						causaltyBranch:
							process?.accident?.segment?.abbreviation,
					},
					false,
					undefined,
					'center',
					'comment-dialog'
				)
					.then(() => {
						reload([])
					})
					.catch(() => null)
			},
		},
		{
			value: 'notification',
			text: i18n`Insert notification`,
			action: () => {
				showDialog(
					'NotificationDialog',
					i18n`Notification Dialog`,
					{
						processId: params?.clinicalProcessId,
					},
					false,
					undefined,
					'center',
					'notification-dialog'
				)
					.then(() => {
						reload([])
					})
					.catch(() => null)
			},
		},
		{
			value: 'note',
			text: i18n`Insert note`,
			action: () => {
				addNote && addNote({ processId: params.clinicalProcessId })
			},
		},
		{
			value: 'medicalOpinion',
			text: i18n`Ask clinical opinion`,
			action: () => {
				showDialog(
					'MedicalOpinionDialog',
					i18n`Medical opinion Submission Dialog`,
					{
						processId: params?.clinicalProcessId,
					},
					false,
					undefined,
					'center',
					'medicalOpinion-dialog'
				)
					.then(() => {
						reload([])
					})
					.catch(() => null)
			},
		},
		{
			value: 'medicalOpinion',
			text: i18n`Ask administrative opinion`,
			action: () => {
				showDialog(
					'ManagementOpinionDialog',
					i18n`Administrative Opinion Submission Dialog`,
					{
						processId: params?.clinicalProcessId,
					},
					false,
					undefined,
					'center',
					'administrative-opinion-dialog'
				)
					.then(() => {
						reload([])
					})
					.catch(() => null)
			},
		},
		{
			value: 'ticket',
			text: i18n`Request ticket`,
			action: () => {
				showDialog(
					'TicketRequestDialog',
					i18n`Ticket Request Dialog`,
					{
						processId: params?.clinicalProcessId,
						branch: processBranch,
					},
					false,
					undefined,
					'center',
					'request-ticket-dialog'
				)
					.then(() => {
						reload([
							`/clinical_processes/${params?.clinicalProcessId}/tasks`,
							`/clinical_processes/${params?.clinicalProcessId}/tickets`,
						])
					})
					.catch(() => null)
			},
		},
		/*CM-182 */
		[
			InjuredState.Accepted,
			InjuredState.Analysis,
			InjuredState.Reopened,
		].includes(process?.processInjuredState) &&
			process?.clinicalStatus?.value !== ClinicalStatus.Discharged &&
			!hasPendingAppointmentsWithPastDates &&
			!hasOpenTransferRequisition && {
				value: 'providerTransfer',
				text: i18n`Request provider transfer`,
				action: () => {
					showDialog(
						'ProcessTransferDialog',
						i18n`Provider Transfer Dialog`,
						{
							processId: params?.clinicalProcessId,
						},
						false,
						undefined,
						'center',
						'provider-transfer-dialog'
					)
						.then(() => {
							reload([])
						})
						.catch(() => null)
				},
			},
		shouldAllowManualIppCreation(process, appointments) && {
			value: 'ippForecast',
			text: i18n`Ask IPP forecast`,
			alreadyPerformed: process?.manualIPPRequest,
			action: () => {
				showDialog(
					'IppForecastDialog',
					i18n`IPP Forecast Submission Dialog`,
					{
						processId: params?.clinicalProcessId,
					},
					false,
					undefined,
					'center',
					'ipp-forecast-dialog'
				)
					.then(() => {
						reload([])
					})
					.catch(() => null)
			},
		},
		// this is the negotiation option
		!process.provider &&
			!hasProcessForwardingTask &&
			[
				InjuredState.Validation,
				InjuredState.withoutAssistance,
				InjuredState.TreatmentOutsideNetwork,
				InjuredState.withoutResponseOnContact,
				InjuredState.OutOfNetworkDischarge,
			].includes(process.processInjuredState) && {
				value: 'forward-task',
				text: i18n`Insert forwarding task`,
				action: () => {
					const task = Task.fromJSON({
						id: 'PROCESS_PROVIDER_FORWARDING',
						entityId: params.clinicalProcessId,
						entityType: 'ClinicalProcess',
						taskType: 'ProcessProviderForwarding',
						state: 'Created',
						process: {
							...rawProcessData,
							insuranceCompany:
								rawProcessData.insurancePolicy
									?.insuranceCompany,
							segment: rawProcessData.accident?.segment
								? {
										id: rawProcessData.accident?.segment
											?.abbreviation,
										name: rawProcessData.accident?.segment
											?.name,
								  }
								: {},
						},
					})

					openSingleTask(task)
				},
			},
		{
			value: 'askTransport',
			text: i18n`Ask transport`,
			action: () => {
				showDialog(
					'CreateTransportDialog',
					i18n`Ask Transport`,
					{
						processId: params?.clinicalProcessId,
					},
					false,
					undefined,
					'center',
					'create-transport-dialog'
				)
					.then(() => {
						// reload([
						// 	`/clinical_processes/${params?.clinicalProcessId}/transport`,
						// ])
					})
					.catch(() => null)
			},
		},
		// {
		// 	value: 'medicalActsNegotiation',
		// 	text: i18n`Medical Acts Valuation`,
		// 	action: () => {
		// 		showDialog(
		// 			'MedicalActsNegotiationDialog',
		// 			i18n`Medical Acts Valuation`,
		// 			{
		// 				processId: params?.clinicalProcessId,
		// 			},
		// 			false,
		// 			undefined,
		// 			'center',
		// 			'medical-acts-valuation-dialog'
		// 		)
		// 			.then(() => {
		// 				reload([
		// 					`/clinical_processes/${params?.clinicalProcessId}/tickets`,
		// 				])
		// 			})
		// 			.catch(() => null)
		// 	},
		// },
	].filter((f) => f)

	const modifyOptions = [
		(sessionUser?.roles || []).includes('admin') ||
		sessionUser?.username === process?.clinicCoordinator?.id
			? {
					value: 'transfer_general_coordinator',
					text: i18n`Change Transfer Coord.`,
					action: () => {
						showDialog(
							'TransferCoordinatorDialog',
							i18n`General Coordinator Transfer Dialog`,
							{
								processId: process?.id,
								coordinatorType: 'general',
							},
							false,
							'transfer_general_coordinator',
							undefined,
							'center'
						)
							.then(() => {
								reload([commentsUri])
							})
							.catch(() => null)
					},
			  }
			: null,
		(sessionUser?.roles || []).includes('admin') ||
		sessionUser?.username === process?.ippCoordinator?.id
			? {
					value: 'transfer_ipp_coordinator',
					text: i18n`Change Transfer IPP Coord.`,
					action: () => {
						showDialog(
							'TransferCoordinatorDialog',
							i18n`IPP Coordinator Transfer Dialog`,
							{
								processId: process?.id,
								coordinatorType: 'ipp',
							},
							false,
							'transfer_ipp_coordinator',
							undefined,
							'center'
						)
							.then(() => {
								reload([commentsUri])
							})
							.catch(() => null)
					},
			  }
			: null,
		(sessionUser?.roles || []).some((role) =>
			['admin', 'supervisor'].includes(role)
		) || sessionUser?.username === process?.trustClaimsManager?.id
			? {
					value: 'transfer_trust_manager',
					text: i18n`Change Trust Manager`,
					action: () => {
						showDialog(
							'TransferCoordinatorDialog',
							i18n`Trust Manager Transfer Dialog`,
							{
								processId: process?.id,
								coordinatorType: 'trust_manager',
							},
							false,
							'transfer_trust_manager',
							undefined,
							'center'
						)
							.then(() => {
								reload([commentsUri])
							})
							.catch(() => null)
					},
			  }
			: null,
		{
			value: 'change_injured_state',
			text: i18n`Change injured state`,
			action: () => {
				showDialog(
					'ChangeInjuredStateDialog',
					i18n`Injured State Change Dialog`,
					{
						processId: process?.id,
					},
					false,
					'change_injured_state',
					undefined,
					'center'
				)
					.then(() => {
						reload([processUri, commentsUri])
					})
					.catch(() => null)
			},
		},
		(isAdministrative || isAdmin) &&
			(process?.accident?.segment?.abbreviation === Segment.WorkRelated ||
				process?.accident?.segment?.abbreviation ===
					Segment.TrafficAccident) &&
			(APPOINTMENT_PROTOCOLS || MEDICAL_ACT_PROTOCOLS) && {
				value: 'edit_process_protocol',
				text: i18n`Edit protocol`,
				action: () => {
					showDialog(
						'EditProcessProtocolDialog',
						i18n`Edit Process Protocols`,
						{
							processId: process?.id,
							accidentType:
								process?.accident?.segment?.abbreviation,
							postalCode: process?.injured?.postalCode,
						},
						false,
						undefined,
						'center',
						'edit_process_protocol'
					).catch(() => null)
				},
			},
		!(
			[InjuredState.Terminated, InjuredState.Canceled].includes(
				process?.processInjuredState
			) || userRoles.includes('doctor')
		) && {
			value: 'edit_injured_data',
			text: i18n`Edit injured data`,
			action: () => {
				showDialog(
					'EditInjuredDataDialog',
					i18n`Edit injured data dialog`,
					{
						injured: process?.injured,
						processId: process?.id,
					},
					false,
					undefined,
					undefined,
					'center'
				)
					.then(() => {
						reload([processUri])
					})
					.catch(() => null)
			},
		},
		!(
			[InjuredState.Terminated, InjuredState.Canceled].includes(
				process?.processInjuredState
			) || userRoles.includes('doctor')
		) && {
			value: 'edit_process_data',
			text: i18n`Edit process data`,
			action: () => {
				showDialog(
					'EditProcessDataDialog',
					i18n`Edit process data dialog`,
					{
						process,
					},
					false,
					undefined,
					undefined,
					'center'
				)
					.then(() => {
						reload([processUri])
					})
					.catch(() => null)
			},
		},
	].filter((elem) => elem)

	const isDocumentsDisabled = useMemo(
		() => documents?.length === 0,
		[documents]
	)

	const isNotesDisabled = useMemo(() => notes?.length === 0, [notes])
	const isTasksDisabled = useMemo(() => tasks?.length === 0, [tasks])
	const isNotificationsDisabled = useMemo(
		() => notifications?.length === 0,
		[notifications]
	)

	const mappedTasks = mapTasks(tasks, i18n, sessionUser)

	const financeOptions = [
		{
			value: 'purchasing_process',
			text: i18n`purchase suggestion`,
		},
		{
			value: 'selling_process',
			text: i18n`sale suggestion`,
		},
	]

	return (
		<>
			<div className={styles['header-title']}>
				<CopyToClipboard
					text={i18n`Copy text`}
					copiedText={i18n`Text copied`}
					clipboardText={params.clinicalProcessId || ''}
					displayArrow={true}
					alwaysBelow={true}
				>
					<h5>#{params.clinicalProcessId}</h5>
				</CopyToClipboard>
				<a
					className={styles['go-to-link']}
					href={`${PORTAL_TRUST_ENDPOINT}?IdProcesso=${params.clinicalProcessId}`}
					target='_blank'
					rel='noreferrer'
				>
					<small>
						<Icon
							className={styles['link-icon']}
							name='link'
							width='15px'
							height='15px'
						/>
					</small>
					<small>
						{process?.accident?.segment?.abbreviation} -{' '}
						{process?.insurancePolicy?.insuranceCompany
							?.internalName ||
							i18n`Insurance Provider Undefined`}
					</small>
				</a>
			</div>
			<HStack className={styles['actions-container']} gap='small'>
				<HStack gap='xsmall' align='center'>
					{(isFinance || isAdmin || isIbReadOnly) && (
						<ModalOptions
							options={financeOptions}
							onSelect={(item) => {
								const targetUrl =
									item?.value === 'purchasing_process'
										? `${CATALOG_WEBAPP_ENDPOINT}/clinical-processes/purchase/${params.clinicalProcessId}`
										: `${CATALOG_WEBAPP_ENDPOINT}/clinical-processes/sale/${params.clinicalProcessId}`

								window.open(targetUrl, '_blank')
							}}
						>
							<ActionButton
								className={`${styles['icon-container']}`}
							>
								<Icon name='euro' height='20px' width='20px' />
							</ActionButton>
						</ModalOptions>
					)}
					<LightBoxButton
						isDisabled={isNotificationsDisabled}
						total={notifications?.length}
						icon='bell'
					>
						{renderNotifications(notifications, i18n)}
					</LightBoxButton>

					<LightBoxButton
						isDisabled={isTasksDisabled}
						total={mappedTasks.length}
						icon='task'
					>
						{renderTasks(mappedTasks, i18n, openSingleTask)}
					</LightBoxButton>

					<LightBoxButton isDisabled={isNotesDisabled} icon='note-v2'>
						{renderNotes(notes, i18n, deleteNote, updateNote)}
					</LightBoxButton>

					<LightBoxButton
						isDisabled={isDocumentsDisabled}
						icon='attach'
					>
						{renderDocuments(documents, i18n, openFile)}
					</LightBoxButton>

					<ModalOptions
						options={insertOptions}
						onSelect={(item) => {
							if (item?.alreadyPerformed) {
								pushNotification(
									undefined,
									'info',
									i18n`There is a FSP already requested!`
								)
							} else {
								item && item.action && item.action()
							}
						}}
					>
						<ActionButton className={styles['icon-container']}>
							<Icon name='plus' height='20px' width='20px' />
						</ActionButton>
					</ModalOptions>
					<ModalOptions
						options={modifyOptions}
						onSelect={(item) => {
							item && item?.action && item?.action()
						}}
					>
						<ActionButton className={styles['icon-container']}>
							<Icon
								name='edit-pencil'
								height='20px'
								width='20px'
							/>
						</ActionButton>
					</ModalOptions>
				</HStack>
			</HStack>
		</>
	)
}

export default ContextualActionsController
